import React, { useEffect, useState } from 'react';
import { API, authFetch } from './api';
import { useAuth } from './AuthContext';
import LogoUploader, { logoSrc } from './LogoUploader';
import AssociationBrokerages from './AssociationBrokerages';
import AssociationBilling from './AssociationBilling';
import AssociationReports from './AssociationReports';

const Card = ({ title, children, className = '' }) => (
  <div className={`bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 ${className}`}>
    <h2 className="text-white font-semibold mb-3">{title}</h2>
    {children}
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-2 py-1 text-sm">
    <span className="text-[#B0BEC5] shrink-0">{label}</span>
    <span className="text-white min-w-0 break-words text-right">{value}</span>
  </div>
);
const Field = ({ label, value, onChange, type = 'text', min }) => (
  <div>
    <label className="block text-xs text-[#B0BEC5] mb-1">{label}</label>
    <input type={type} value={value ?? ''} min={min} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
  </div>
);

const NUMERIC_KEYS = ['per_seat_price_re', 'per_seat_price_cre', 'payment_terms_days', 'default_trial_days'];
const SETTINGS_KEYS = [...NUMERIC_KEYS, 'remit_to_name', 'remit_to_address', 'remit_to_instructions',
  'billing_contact_name', 'billing_contact_email'];

// Full-screen console shell for association admins (role: association_admin).
// No app (agent/broker) navigation — association admins see only their own
// association's account information and billing/trial settings.
export default function AssociationConsole() {
  const { user, logout } = useAuth();
  const [assoc, setAssoc] = useState(null);
  const [error, setError] = useState('');

  const load = () => authFetch(`${API}/api/associations/me`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load association'))))
    .then(setAssoc).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      <nav className="bg-[#1A2744] border-b border-[#546E7A]/30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex justify-between items-center gap-2">
          <div className="flex items-center gap-3 min-w-0">
            {assoc?.logo_url && <img src={logoSrc(assoc.logo_url)} alt="" className="h-8 w-auto rounded shrink-0" />}
            <div className="text-xl truncate">
              <span className="font-bold text-white">Scheme</span>
              <span className="font-bold text-[#00D4FF]">Sync</span>
              <span className="font-bold text-[#00E676] ml-1.5 text-base">Association</span>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden sm:block text-sm text-white truncate">{user?.full_name}</span>
            <span className="px-2 py-0.5 rounded text-xs bg-purple-600 text-purple-100 whitespace-nowrap">Association Admin</span>
            <button onClick={logout}
              className="px-3 py-1.5 text-sm text-gray-400 hover:text-white hover:bg-red-600/20 rounded-md transition-colors">
              Logout
            </button>
          </div>
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-8">
        {error && <p className="text-red-400 mb-4">{error}</p>}
        {!assoc ? (
          <p className="text-[#B0BEC5]">Loading…</p>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-6">
              {assoc.logo_url && <img src={logoSrc(assoc.logo_url)} alt="" className="h-12 w-auto rounded shrink-0" />}
              <div className="min-w-0">
                <h1 className="text-white text-2xl sm:text-3xl font-bold break-words">{assoc.display_name}</h1>
                <p className="text-[#B0BEC5] break-words">{assoc.name}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <Card title="Licensing">
                <Row label="Products" value={[assoc.licensed_re && 'RE', assoc.licensed_cre && 'CRE'].filter(Boolean).join(' + ') || '—'} />
                <Row label="Status" value={assoc.status} />
              </Card>
              <Card title="Subscription (from AiDA)">
                <Row label="Monthly support fee" value={assoc.monthly_support_fee != null ? `$${assoc.monthly_support_fee}` : '—'} />
                <Row label="Term" value={`${assoc.term_start || '—'} → ${assoc.term_end || '—'}`} />
                <Row label="Launch period" value={`${assoc.launch_period_days} days`} />
                <Row label="AiDA billing starts" value={assoc.aida_billing_start || '—'} />
              </Card>
            </div>
            <Card title="Branding" className="mb-4">
              <p className="text-xs text-[#B0BEC5] mb-2">Your logo appears on your header and (soon) your brokerage signup page.</p>
              <LogoUploader associationId={assoc.id} currentUrl={assoc.logo_url}
                onUploaded={(url) => setAssoc({ ...assoc, logo_url: url })} />
            </Card>
            <SettingsForm assoc={assoc} onSaved={setAssoc} />
            <div className="mt-4"><AssociationBrokerages assoc={assoc} /></div>
            <div className="mt-4"><AssociationBilling assoc={assoc} /></div>
            <div className="mt-4"><AssociationReports assoc={assoc} /></div>
            <Card title="Association Admins" className="mt-4">
              {(assoc.admins || []).map((a) => (
                <div key={a.id} className="flex justify-between gap-2 py-1.5 border-b border-[#546E7A]/20 text-sm last:border-0">
                  <span className="text-white min-w-0 break-words">{a.full_name} <span className="text-gray-500">({a.email})</span></span>
                  <span className="text-[#B0BEC5] shrink-0">{a.has_password ? 'Active' : 'Invite pending'}</span>
                </div>
              ))}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function SettingsForm({ assoc, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(SETTINGS_KEYS.map((k) => [k, assoc[k] ?? ''])));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setMsg('');
    try {
      const body = {};
      for (const k of SETTINGS_KEYS) {
        const v = form[k];
        if (v === '' || v == null) continue;
        body[k] = NUMERIC_KEYS.includes(k) ? Number(v) : v;
      }
      const res = await authFetch(`${API}/api/associations/${assoc.id}/settings`,
        { method: 'PATCH', body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      onSaved(data);
      setMsg('Saved.');
    } catch (err) { setMsg(err.message); } finally { setSaving(false); }
  };

  return (
    <Card title="Billing & Trial Settings">
      <form onSubmit={save} className="grid md:grid-cols-2 gap-3">
        <Field label="Per-seat price — RE ($)" type="number" min="0" value={form.per_seat_price_re} onChange={(v) => set('per_seat_price_re', v)} />
        <Field label="Per-seat price — CRE ($)" type="number" min="0" value={form.per_seat_price_cre} onChange={(v) => set('per_seat_price_cre', v)} />
        <Field label="Payment terms (Net XX days)" type="number" min="0" value={form.payment_terms_days} onChange={(v) => set('payment_terms_days', v)} />
        <Field label="Default trial length (days; 0 = none)" type="number" min="0" value={form.default_trial_days} onChange={(v) => set('default_trial_days', v)} />
        <Field label="Remit-to name" value={form.remit_to_name} onChange={(v) => set('remit_to_name', v)} />
        <Field label="Remit-to address" value={form.remit_to_address} onChange={(v) => set('remit_to_address', v)} />
        <Field label="Payment instructions (printed on invoices)" value={form.remit_to_instructions} onChange={(v) => set('remit_to_instructions', v)} />
        <Field label="Billing contact name" value={form.billing_contact_name} onChange={(v) => set('billing_contact_name', v)} />
        <Field label="Billing contact email" value={form.billing_contact_email} onChange={(v) => set('billing_contact_email', v)} />
        <div className="md:col-span-2 flex items-center gap-3">
          <button type="submit" disabled={saving}
            className="px-4 py-2 rounded-md bg-[#00D4FF] text-[#0A0E27] font-semibold disabled:opacity-50">
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
          {msg && <span className="text-sm text-[#00E676]">{msg}</span>}
        </div>
      </form>
    </Card>
  );
}
